/* iter87.199.137 — Subscribe to Pro+ banner (Paddle).

Shown only to Clerk-authenticated users whose effective tier is
"free". Grandfathered / paid / comp users don't see it. Clicking
fetches `/api/billing/paddle/checkout-config` (backend derives email
from the Clerk session + returns the price id, a public client-side
token, and which Paddle environment to talk to), then opens Paddle's
client-side checkout overlay directly in the browser — there's no
server-created checkout URL to redirect/open.
Post-payment the Paddle webhook grants Pro+ and `/billing/success`
polls until the flip lands, same as before. */
import React, { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import ProPlusBillingTermsModal from "@/components/ProPlusBillingTermsModal";
import { ensurePaddleInitialized } from "@/lib/paddleClient";

const API = process.env.REACT_APP_BACKEND_URL;


export default function SubscribeProPlus({ compact = false }) {
    const { isSignedIn, getToken } = useAuth();
    const [configured, setConfigured] = useState(null); // null=loading, bool
    const [loading, setLoading] = useState(false);
    const [accepted, setAccepted] = useState(false); // iter87.199.97 — terms checkbox
    const [billingTermsOpen, setBillingTermsOpen] = useState(false); // iter87.199.145 — in-flow terms modal
    const loadingRef = useRef(loading);
    loadingRef.current = loading;

    useEffect(() => {
        let cancel = false;
        fetch(`${API}/api/billing/paddle/status`)
            .then((r) => r.ok ? r.json() : { configured: false })
            .then((j) => { if (!cancel) setConfigured(!!j.configured); })
            .catch(() => { if (!cancel) setConfigured(false); });
        return () => { cancel = true; };
    }, []);

    function onPaddleEvent(evt) {
        const name = evt?.name || "";
        if (name === "checkout.completed") {
            window.location.assign("/billing/success");
        } else if (name === "checkout.closed") {
            // User dismissed the overlay before paying — release the
            // button so they can retry.
            if (loadingRef.current) setLoading(false);
        }
    }

    async function subscribe() {
        if (!isSignedIn) {
            toast.error("Sign in first, then subscribe.");
            return;
        }
        if (!accepted) {
            toast.error("Please accept the Pro+ Billing Terms first.");
            return;
        }
        setLoading(true);
        try {
            const token = await getToken();
            const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
            // iter87.199.97 — Record acceptance BEFORE requesting checkout.
            // Backend refuses checkout with 428 if the current-version
            // acceptance isn't on record. iter87.199.141 — terms endpoints
            // moved to live under /billing/paddle/* directly (Lemon
            // Squeezy, which they used to borrow the prefix from, is gone).
            const vr = await fetch(`${API}/api/billing/paddle/terms-version`);
            const { version } = await vr.json();
            const ar = await fetch(`${API}/api/billing/paddle/accept-terms`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeaders },
                credentials: "include",
                body: JSON.stringify({ version }),
            });
            if (!ar.ok) {
                const t = await ar.text();
                throw new Error(t || `accept-terms failed (${ar.status})`);
            }
            const r = await fetch(`${API}/api/billing/paddle/checkout-config`, {
                headers: { ...authHeaders },
                credentials: "include",
            });
            if (r.status === 409) {
                toast.info("Pro+ is already active on this account.");
                return;
            }
            if (!r.ok) {
                const body = await r.text();
                throw new Error(body || `checkout-config failed (${r.status})`);
            }
            const { price_id, client_token, customer_email, environment } = await r.json();
            await ensurePaddleInitialized(client_token, environment, onPaddleEvent);
            window.Paddle.Checkout.open({
                items: [{ priceId: price_id, quantity: 1 }],
                customer: { email: customer_email },
                customData: { app_email: customer_email },
            });
            // Loading stays true until `checkout.closed` (release) or
            // `checkout.completed` (we're navigating away) fires above.
        } catch (e) {
            toast.error(e?.message || "Unable to open checkout");
            setLoading(false);
        }
    }

    // If Paddle isn't wired yet, hide entirely — no dead button.
    if (configured === false) return null;

    if (compact) {
        return (
            <div className="space-y-2" data-testid="subscribe-pro-plus-compact">
                <label className="flex items-start gap-2 text-[11px] text-white/70 cursor-pointer">
                    <input
                        type="checkbox" checked={accepted}
                        onChange={(e) => setAccepted(e.target.checked)}
                        data-testid="subscribe-terms-checkbox-compact"
                        className="mt-0.5 accent-cyber"
                    />
                    <span>
                        I agree to the{" "}
                        <button type="button" onClick={() => setBillingTermsOpen(true)}
                           className="text-cyber underline hover:text-cyber/80"
                           data-testid="subscribe-terms-link-compact">
                            Pro+ Billing Terms
                        </button>
                    </span>
                </label>
                <button
                    onClick={subscribe}
                    disabled={loading || configured === null || !accepted}
                    data-testid="subscribe-pro-plus-compact-btn"
                    className="brutal-btn-secondary text-xs px-4 py-2 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {loading ? "Opening…" : "Subscribe $5/mo"}
                </button>
                <ProPlusBillingTermsModal open={billingTermsOpen} onClose={() => setBillingTermsOpen(false)} />
            </div>
        );
    }

    return (
        <section
            data-testid="subscribe-pro-plus-banner"
            className="brutal-card border-volt/50 bg-gradient-to-r from-volt/10 to-volt/5 p-5 mb-6 space-y-4"
        >
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                    <div className="text-xs uppercase tracking-widest text-volt font-black mb-1">
                        Upgrade to Pro+
                    </div>
                    <div className="text-lg font-black tracking-tight">
                        Unlock Ride Stories, Recaps, and Living Rank charts
                    </div>
                    <div className="text-xs text-white/60 mt-1">
                        $5/month · cancel anytime · your rivalries stay yours forever
                    </div>
                </div>
                <button
                    onClick={subscribe}
                    disabled={loading || configured === null || !accepted}
                    data-testid="subscribe-pro-plus-btn"
                    className="brutal-btn-secondary text-sm px-6 py-3 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {loading ? "Opening checkout…" : "Subscribe · $5/mo"}
                </button>
            </div>
            <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer border-t border-white/10 pt-3">
                <input
                    type="checkbox" checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    data-testid="subscribe-terms-checkbox"
                    className="mt-0.5 accent-cyber"
                />
                <span>
                    I understand this is a recurring <strong className="text-white">$5/month</strong> charge and I agree to the{" "}
                    <button type="button" onClick={() => setBillingTermsOpen(true)}
                       className="text-cyber underline hover:text-cyber/80"
                       data-testid="subscribe-terms-link">
                        Pro+ Billing Terms
                    </button>.
                </span>
            </label>
            <ProPlusBillingTermsModal open={billingTermsOpen} onClose={() => setBillingTermsOpen(false)} />
        </section>
    );
}
